import classNames from 'classnames';
import React, { useState } from 'react';

import { IconTypes } from '@eika/icon';

import type { AccountNumber } from '../../../../generated-types';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { EikaModal } from '../../../EikaModal';
import {
    AmountInput,
    FromAccountSelect,
    OwntextInput,
    ReceiverComboBox,
} from '../../../NettbankCreateTransactions/components';
import useReceiverSelector from '../../../NettbankCreateTransactions/components/ReceiverComboBox/useReceiverSelector';
import { MessageInput } from '../../../fields';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import NewReceiver from '../../Payment/SelectReceiver/NewReceiver';
import { SelectedReceiver } from '../../SelectedReceiver';
import { AvtaleGiroActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { MAX_NOTE_LIMIT } from '../form/validate';
import { FieldProps } from './FieldProps';

const AvtalegiroDesktopFields: React.FC<FieldProps> = ({
    locked,
    receiverLock,
    kidLock,
    accounts,
    invalidAccounts,
    accountState,
    freeWithdrawals,
    animateModalAs,
    emptyFieldWarningLabel,
    bottomLabel,
}) => {
    const translated = useTranslation();
    const [searchString, setSearchString] = useState('');
    const [showAddReceiver, setShowAddReceiver] = useState(false);
    const { state, handleChange, curryChange, errors } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();
    const [placeholderHeight, setPlaceholderHeight] = useState<number>(0);

    const { toAccountErrorMessage, selectReceiver, ocr, receivers, waitingForReceivers } = useReceiverSelector(
        { value: state.toAccount, error: errors.toAccount },
        curryChange<SelectedReceiver | undefined>(AvtaleGiroActionType.TO_ACCOUNT_SELECTED),
        setSearchString,
        locked,
    );

    const cleanKID = (value: string) => value.replace(/[^0-9-]/g, '');

    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector open', {
                    error: errors.accountId || errors.toAccount || toAccountErrorMessage,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                id="avtalegiro-from-account"
                label={translated.paymentFrom}
                placeholder={translated.payFromAccount}
                items={accounts}
                invalidItems={invalidAccounts}
                onSelect={curryChange<AccountNumber>(AvtaleGiroActionType.FROM_ACCOUNT_SELECTED)}
                locked={locked}
                isLoading={accountState.isLoading}
                isPaymentModalOpen
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                bottomLabel={emptyFieldWarningLabel ?? bottomLabel}
                setPlaceholderHeight={setPlaceholderHeight}
            />
            <ReceiverComboBox
                className={classNames('transaction-form__to-account-selector open', {
                    error: errors.toAccount || errors.accountId || toAccountErrorMessage,
                    'has-withdrawal': freeWithdrawals.show,
                })}
                id="payment-to-account"
                label={translated.to}
                placeholder={translated.addReceiver}
                searchString={
                    searchString.length === 0 && state.toAccount?.accountName
                        ? state.toAccount?.accountName
                        : searchString
                }
                setSearchString={(value: string) => setSearchString(value)}
                icon={IconTypes.PERSONER_24}
                items={receivers}
                onSelect={(item?: SelectedReceiver) => selectReceiver(item)}
                selected={state.toAccount}
                locked={receiverLock || locked}
                isLoading={waitingForReceivers || ocr.isPending}
                onShowAddReceiver={() => setShowAddReceiver(true)}
                addReceiverText={translated.addNewReceiver}
                error={toAccountErrorMessage}
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
            />
            <AmountInput
                className={classNames('transaction-form__amount-selector', {
                    'transaction-form__amount-selector--error': errors.transactionLimit,
                })}
                value={state.transactionLimit}
                label={translated.avtaleGiroAgreement.limitAmountText}
                error={errors.transactionLimit}
                onChange={curryChange<number | undefined>(AvtaleGiroActionType.TRANSACTION_AMOUNT_LIMIT)}
                isNoCoverage={false}
                locked={locked}
            />
            <AmountInput
                className={classNames('transaction-form__amount-selector', 'transaction-form__amount-limit-selector', {
                    'transaction-form__amount-selector--error': errors.monthlyLimit,
                })}
                value={state.monthlyLimit}
                label={translated.avtaleGiroAgreement.monthlyLimitAmountText}
                error={errors.monthlyLimit}
                onChange={curryChange<number | undefined>(AvtaleGiroActionType.MONTHLY_AMOUNT_LIMIT)}
                isNoCoverage={false}
                locked={locked}
            />
            <MessageInput
                label={translated.avtaleGiroAgreement.kidField}
                placeholder={translated.avtaleGiroAgreement.kidPlaceholder}
                className={classNames('transaction-form__message-selector', { error: errors.kid })}
                kidValue={state.kid?.value ?? ''}
                error={errors.kid}
                onChangeKid={(value: string) =>
                    handleChange({
                        type: AvtaleGiroActionType.SET_KID,
                        payload: { value: cleanKID(value) },
                    })
                }
                maxLength={MAX_NOTE_LIMIT}
                locked={locked || kidLock}
                selectedReceiver={state.toAccount}
                isKidOnly
            />
            <OwntextInput
                ownText={state.note}
                onChangeOwntext={curryChange(AvtaleGiroActionType.NOTE)}
                locked={locked}
                className="transaction-form__owntext-selector"
                label={translated.avtaleGiroAgreement.ownText}
                placeholder={translated.avtaleGiroAgreement.ownTextPlaceholder}
                avtaleGiro
            />
            <DesktopDropdownPlaceholder height={placeholderHeight} />
            <EikaModal
                id="payment-add-new-receiver-modal"
                open={showAddReceiver}
                setOpen={setShowAddReceiver}
                animateAs={animateModalAs}
            >
                <NewReceiver
                    receivers={receivers}
                    searchString={searchString}
                    onChange={(selectedReceiver) => {
                        setShowAddReceiver(false);
                        setSearchString(selectedReceiver.accountName ?? '');
                        handleChange({
                            type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED,
                            payload: selectedReceiver,
                        });
                    }}
                    isNettbank
                    onCancel={() => setShowAddReceiver(false)}
                />
            </EikaModal>
        </>
    );
};

export default AvtalegiroDesktopFields;
